package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.platform.testTag
import kotlin.math.roundToInt

/**
 * SH-154: [header] above [content] that slides away while the content scrolls up and comes back
 * when it scrolls down to its top (any scrollable inside [content]: lists, tables, wheel, touch),
 * so notes above a table don't take the height of a short screen, e.g. a phone held sideways.
 */
@Composable
fun CollapsingHeaderLayout(
    header: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit
) {
    var headerHeight by remember { mutableIntStateOf(0) }
    var offset by remember { mutableFloatStateOf(0f) }
    val connection = remember {
        object : NestedScrollConnection {
            fun take(dy: Float): Offset {
                val next = (offset + dy).coerceIn(-headerHeight.toFloat(), 0f)
                val used = next - offset
                offset = next
                return Offset(0f, used)
            }

            // Scrolling the content up: the header goes first.
            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset =
                if (available.y < 0) take(available.y) else Offset.Zero

            // Scrolling down: the header comes back once the content is at its top.
            override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset =
                if (available.y > 0) take(available.y) else Offset.Zero
        }
    }
    Layout(
        modifier = modifier.fillMaxSize().clipToBounds().nestedScroll(connection),
        content = {
            Box(Modifier.testTag(COLLAPSING_HEADER_TAG)) { header() }
            Box { content() }
        }
    ) { measurables, constraints ->
        val loose = constraints.copy(minWidth = 0, minHeight = 0)
        val top = measurables[0].measure(loose)
        headerHeight = top.height
        val shown = (top.height + offset.coerceAtLeast(-top.height.toFloat())).roundToInt().coerceAtLeast(0)
        val body = measurables[1].measure(
            constraints.copy(minHeight = 0, maxHeight = (constraints.maxHeight - shown).coerceAtLeast(0))
        )
        layout(constraints.maxWidth, constraints.maxHeight) {
            top.place(0, shown - top.height)
            body.place(0, shown)
        }
    }
}

const val COLLAPSING_HEADER_TAG = "collapsing_header"
